import React, { FunctionComponent, memo } from 'react';
import { Box, Grid, Typography } from '@material-ui/core';
import IExtendedAccessory from '../../../../../model/accessories/IExtendedAccessory';
import Image from 'material-ui-image';
import CarUnavailable from '../../../../../../common/assets/images/carUnavailable.png';
import accessoryItemStyles from './accessoryItemStyles';
import Checkbox from '@material-ui/core/Checkbox';
import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery';
import useTheme from '@material-ui/core/styles/useTheme';
import ConfigurablePrice from '../../../../../../common/components/configurablePrice/ConfigurablePrice';
import clsx from 'clsx';
import CustomTitle from '../../../../presentational/customTitle/CustomTitle';
import { ISpcTheme } from '../../../../../../common/theme/ISpcTheme';
import { useIntl } from 'react-intl';
import HandleFeatureAccessoryDataLayerRest from '../../../../../../common/utils/dataLayer/rest/handleFeatureAccessoryDataLayerRest';
import useWidgetContext from '../../../../../../common/react/useWidgetContext';

export interface IAccessoryItemProps {
  accessory: IExtendedAccessory;
  /**
   * Event used to handle logic on selecting the accessory
   */
  onClick: (name: string) => void;
  isSelected?: boolean;
  isSelectable?: boolean;
  inModal?: boolean;
}

const AccessoryItem: FunctionComponent<IAccessoryItemProps> = memo(
  ({ accessory, onClick, isSelectable = true, inModal = false, isSelected }) => {
    const styles = accessoryItemStyles();
    const theme = useTheme<ISpcTheme>();
    const {
      spc: {
        customise: { optionPriceLabel },
      },
    } = useTheme<ISpcTheme>();
    const isMobile = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));
    const intl = useIntl();
    const {
      configuration: { dimensions },
    } = useWidgetContext();

    // @TODO: Fix rerender issue when toggling accessories.
    // console.log('accessory rerender count.');
    const handleFeaturesDatalayerHandler = (accessory: IExtendedAccessory) => {
      onClick(accessory.id);
      HandleFeatureAccessoryDataLayerRest(accessory.name, !isSelected, dimensions);
    };

    return (
      <Grid item={true} xs={inModal && !isMobile ? 4 : 6} className={styles.gridContainer}>
        <Box
          className={styles.container}
          data-test={`container:option:${accessory.id}`}
          onClick={() => handleFeaturesDatalayerHandler(accessory)}
        >
          <Image
            key={accessory.image.url}
            src={accessory.image.url || 'error'}
            alt={accessory.name}
            animationDuration={0}
            color="transparent"
            aspectRatio={1.75}
            errorIcon={<img src={CarUnavailable} data-test={'image:vehicle:unavailable'} />}
          />
          <Box className={clsx([styles.body, { [styles.smallBody]: inModal }])}>
            <Typography variant="caption" className={styles.accessoryTitle}>
              <CustomTitle
                title={accessory.name}
                indicative={intl.formatMessage({ id: 'spc.customise.indicative.option' })}
              />
            </Typography>
            <ConfigurablePrice
              prices={accessory.prices}
              display={{ optionPriceLabel }}
              classes={{ multi: { priceText: styles.priceText } }}
            />
          </Box>
          {isSelectable && (
            <Box
              className={styles.accessoryCheckContainer}
              data-test={`accessory:checkbox:${accessory.id}`}
              data-selected={`${isSelected}`}
            >
              <Checkbox checked={isSelected} />
            </Box>
          )}
        </Box>
      </Grid>
    );
  },
);

export default AccessoryItem;
